import { Controller } from "@hotwired/stimulus"
import { loadMapLibre, loadBaseStyle, createPin, escapeHtml } from "../lib/maplibre"

// An edition's listings on a map: one pin per listing in its type's colour (one dark pin with a count
// where several share a spot). A pin opens its listing in the drawer; a shared pin lists them first.
export default class extends Controller {
  static targets = ["container", "status"]
  static values = { points: Array }

  connect() {
    this.generation = (this.generation || 0) + 1
    if (this.pointsValue.length === 0) {
      this.statusTarget.textContent = "None of these listings has a pin yet."
      return
    }
    this.load(this.generation)
  }

  disconnect() {
    this.generation++
    this.map?.remove()
    this.map = null
  }

  async load(generation) {
    try {
      const [maplibregl, style] = await Promise.all([loadMapLibre(), loadBaseStyle()])
      if (generation !== this.generation) return
      this.draw(maplibregl, style)
      this.statusTarget.hidden = true
    } catch (error) {
      console.warn("Map unavailable:", error)
      this.statusTarget.textContent = "The map couldn't load here. The list view has every listing."
    }
  }

  draw(maplibregl, style) {
    this.containerTarget.replaceChildren()
    this.map = new maplibregl.Map({
      container: this.containerTarget,
      style,
      attributionControl: { compact: true },
      cooperativeGestures: true,
      dragRotate: false,
      pitchWithRotate: false
    })
    this.map.touchZoomRotate.disableRotation()
    this.map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right")

    const bounds = new maplibregl.LngLatBounds()
    this.groups().forEach((points) => {
      const [first] = points
      const pin = createPin({ color: first.colour, count: points.length, label: points.length > 1 ? `${points.length} listings` : first.title })
      const marker = new maplibregl.Marker({ element: pin, anchor: "bottom" }).setLngLat([first.lng, first.lat]).addTo(this.map)
      if (points.length === 1) {
        pin.addEventListener("click", () => this.open(first))
      } else {
        marker.setPopup(new maplibregl.Popup({ offset: 28, closeButton: false, maxWidth: "280px" }).setDOMContent(this.list(points)))
      }
      bounds.extend([first.lng, first.lat])
    })
    this.map.fitBounds(bounds, { padding: 48, maxZoom: 15, duration: 0 })
  }

  // Listings within a few metres of each other share a pin
  groups() {
    const groups = new Map()
    this.pointsValue.forEach((point) => {
      const key = `${point.lat.toFixed(4)},${point.lng.toFixed(4)}`
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key).push(point)
    })
    return [...groups.values()]
  }

  list(points) {
    const list = document.createElement("ul")
    list.className = "max-h-64 overflow-y-auto text-[12.5px]"
    list.innerHTML = points.map((point, index) => `
      <li><button type="button" data-index="${index}" class="flex w-full flex-col items-start rounded-md px-2 py-1.5 text-left hover:bg-stone-100">
        <span class="font-medium text-stone-900">${escapeHtml(point.title)}</span>
        <span class="text-stone-500">${escapeHtml(point.meta)}</span>
      </button></li>`).join("")
    list.addEventListener("click", (event) => {
      const button = event.target.closest("button[data-index]")
      if (button) this.open(points[Number(button.dataset.index)])
    })
    return list
  }

  // Through a drawer link, so the drawer opens (and the URL updates) as it does from the list
  open(point) {
    const link = document.createElement("a")
    link.href = point.url
    link.dataset.turboFrame = "drawer"
    link.dataset.drawerItem = point.slug
    link.hidden = true
    this.element.appendChild(link)
    link.click()
    link.remove()
  }
}
